/**
 * CounselCore Firebase Configuration
 * Handles Firebase Cloud Messaging (FCM) for push notifications
 * 
 * Created: January 6, 2026
 */

import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

let firebaseApp = null;
let messaging = null;

/**
 * Initialize Firebase with organization-specific config
 * @param {Object} config - Firebase configuration from org settings
 * @returns {Object|null} - Firebase messaging instance or null
 */
export const initializeFirebase = async (config) => {
  try {
    // Check if messaging is supported in this browser
    const supported = await isSupported();
    if (!supported) {
      console.warn('Firebase Messaging is not supported in this browser');
      return null;
    }

    if (!config?.apiKey || !config?.projectId) {
      console.warn('Firebase config missing required fields');
      return null;
    }

    // Only initialize if not already done
    if (getApps().length === 0) {
      firebaseApp = initializeApp({
        apiKey: config.apiKey,
        authDomain: `${config.projectId}.firebaseapp.com`,
        projectId: config.projectId,
        storageBucket: `${config.projectId}.appspot.com`,
        messagingSenderId: config.senderId || '',
        appId: config.appId || ''
      });
    } else {
      firebaseApp = getApps()[0];
    }

    messaging = getMessaging(firebaseApp);
    console.log('Firebase initialized successfully');
    return messaging;
  } catch (error) {
    console.error('Failed to initialize Firebase:', error);
    return null;
  }
};

/**
 * Request notification permission and get FCM token
 * @param {string} vapidKey - VAPID key from Firebase console
 * @returns {Object} - { success: boolean, token?: string, error?: string }
 */
export const requestNotificationPermission = async (vapidKey) => {
  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      return { success: false, error: 'Notifications not supported in this browser' };
    }

    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission === 'denied') {
      return { success: false, error: 'Notification permission denied' };
    }
    
    if (permission !== 'granted') {
      return { success: false, error: 'Notification permission not granted' };
    }

    if (!messaging) {
      return { success: false, error: 'Firebase not initialized' };
    }

    // Get FCM token
    const token = await getToken(messaging, { 
      vapidKey: vapidKey,
      serviceWorkerRegistration: await navigator.serviceWorker.register('/firebase-messaging-sw.js')
    });

    if (token) {
      console.log('FCM Token obtained:', token.substring(0, 20) + '...');
      return { success: true, token };
    } else {
      return { success: false, error: 'No FCM token available' };
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Listen for foreground messages
 * @param {Function} callback - Function to call when message received
 * @returns {Function} - Unsubscribe function
 */
export const onForegroundMessage = (callback) => {
  if (!messaging) {
    console.warn('Firebase messaging not initialized');
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
};

/**
 * Check if notifications are supported and permission status
 * @returns {Object} - { supported: boolean, permission: string }
 */
export const getNotificationStatus = () => {
  const supported = 'Notification' in window;
  const permission = supported ? Notification.permission : 'unsupported';
  
  return { supported, permission };
};

/**
 * Check if Firebase messaging is initialized
 * @returns {boolean}
 */
export const isFirebaseInitialized = () => {
  return messaging !== null;
};
